import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import { EMOTE_NAMES, type EmoteName } from '../../../generated/emotes';
import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { scrollbarThin } from '../../theme/scrollbar.css';
import { tokens } from '../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../UIState';
import { bindValtio } from '../../ValtioController';
import { GridPicker, type Searchable, gridPickerStyles } from '../grid-picker';

interface EmoteEntry extends Searchable {
  readonly name: EmoteName;
}

/** Each emote as the search sees it, built once. */
const EMOTE_ENTRIES: readonly EmoteEntry[] = EMOTE_NAMES.map((name) => ({ name, searchText: name, names: [name] }));

/**
 * Emotes Panel - Left-side grid picker for sending emote SAY bubbles
 *
 * Positioned above the chatbox on the left side of the screen.
 * Clicking an emote sends it as a SAY command and closes the panel.
 * The search and keyboard picking are GridPicker's, shared with the menu's
 * flag page (flag-picker).
 *
 * @element emotes-panel
 */
@customElement('emotes-panel')
export class EmotesPanel extends LitElement {
  static override styles = [
    tokens,
    gridPickerStyles,
    scrollbarThin,
    css`
      :host {
        pointer-events: auto;
        /* Declared here rather than inherited from the body: the panel owns
           its type, and a slow tap on an emote shouldn't start a text
           selection, callout or grey tap flash. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        --picker-item-width: 72px;
        --picker-item-height: 70px;
      }

      /* Open, it takes the chat stack's free height above the chat box. */
      :host([open]) {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }

      .panel {
        width: 100%;
        box-sizing: border-box;
        flex: 1;
        min-height: 0;
        background-color: var(--color-bg-modal);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        padding: 12px;
        margin-bottom: 6px;
        cursor: default;
        display: flex;
        flex-direction: column;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 10px;
      }

      .title {
        font-weight: bold;
        font-size: var(--font-size-16);
        color: var(--color-text-white);
      }

      .close-btn {
        position: relative;
        display: flex;
        align-items: center;
        padding: 4px;
        background: transparent;
        border: 0;
        border-radius: var(--radius-sm);
        cursor: pointer;
        opacity: 0.5;
        transition: opacity 0.15s;
      }

      /* Gated so iOS sticky-hover doesn't leave a tapped ✕ lit. */
      @media (hover: hover) {
        .close-btn:hover {
          opacity: 1;
          background-color: var(--color-bg-hover);
        }
      }

      .close-btn:active {
        opacity: 1;
        background-color: var(--color-bg-hover);
      }

      /* A 44px tap target on touch screens, around the 22px button. */
      @media (hover: none) {
        .close-btn::after {
          content: '';
          position: absolute;
          inset: -11px;
        }
      }

      .close-btn img {
        width: 14px;
        height: 14px;
        filter: invert(1);
      }

      .hint {
        font-size: var(--font-size-xs);
        color: var(--color-text-muted);
        padding-bottom: 8px;
      }

      .hint strong {
        color: var(--color-text-link);
      }

      .item img {
        width: 48px;
        height: 48px;
      }

      .item .label {
        font-size: var(--font-size-xxs);
        margin-top: 2px;
        max-width: 56px;
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  @query('.panel') private panel?: HTMLElement;

  @query('.grid') private grid?: HTMLElement;

  @query('input') private searchInput?: HTMLInputElement;

  private readonly picker = new GridPicker(this, {
    items: EMOTE_ENTRIES,
    onPick: (emote) => this.sendEmote(emote.name),
    onClose: () => this.close(),
    grid: () => this.grid,
  });

  private isClosing = false;
  private wasVisible = false;
  private closeTween?: gsap.core.Tween;

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleOutsideClick);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleOutsideClick);
    this.closeTween?.kill();
    if (this.panel) gsap.killTweensOf(this.panel);
  }

  override willUpdate() {
    // Reset the search when the panel opens. Done here, before the render,
    // so the first frame of an open already shows the empty search. The
    // DOM-dependent open effects (fade-in, focus) stay in updated().
    if (this.uiState.activePanel === 'emotes' && !this.wasVisible) {
      this.picker.reset();
    }
  }

  override updated() {
    const isVisible = this.uiState.activePanel === 'emotes';
    // The rest happens on open and close only, not on each of the renders in
    // between (any uiState change re-renders this panel).
    if (isVisible === this.wasVisible) return;
    this.wasVisible = isVisible;
    this.toggleAttribute('open', isVisible);
    if (!isVisible) return;
    this.isClosing = false;
    this.closeTween?.kill();
    this.closeTween = undefined;
    if (this.panel) {
      gsap.fromTo(this.panel, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.25, ease: 'power2.out' });
    }
    // Ready to type into on a keyboard. On a phone or tablet, focus would
    // throw the on-screen keyboard up over the grid, so the box waits for a tap.
    if (!this.uiState.isMobile) {
      requestAnimationFrame(() => this.searchInput?.focus());
    }
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (this.uiState.activePanel !== 'emotes') return;
    if (e.key === 'Escape') {
      this.close();
    }
  };

  private handleOutsideClick = (e: MouseEvent) => {
    if (this.uiState.activePanel !== 'emotes') return;
    if (!e.composedPath().includes(this)) {
      this.close();
    }
  };

  /** The ✕. Escape and a click outside the panel close it silently. */
  private readonly handleCloseClick = () => {
    this.api.playClick();
    this.close();
  };

  /** A clicked or tapped emote. Enter sends one without the click sound. */
  private readonly handleItemClick = (e: Event) => {
    const emote = this.picker.itemFrom(e.currentTarget);
    if (emote === undefined) return;
    this.api.playClick();
    this.sendEmote(emote.name);
  };

  private close() {
    if (this.isClosing) return;
    this.isClosing = true;
    this.closeTween?.kill();
    if (this.panel) {
      this.closeTween = gsap.to(this.panel, {
        opacity: 0,
        y: 20,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: () => {
          this.closeTween = undefined;
          // Only close if we're still the active panel. If another panel took
          // over during the 0.2s fade (e.g. a click that closed emotes also
          // opened the menu), closePanel() would close THAT one instead.
          if (this.uiState.activePanel === 'emotes') {
            this.api.closePanel();
          } else {
            this.isClosing = false;
          }
        },
      });
    } else {
      this.api.closePanel();
    }
  }

  private sendEmote(name: EmoteName) {
    this.api.submitChatMessage('/' + name);
    this.close();
  }

  override render() {
    if (this.uiState.activePanel !== 'emotes') return nothing;

    const results = this.picker.results;

    return html`
      <div class="picker">
        <div class="panel">
          <div class="header">
            <span class="title">EMOTES</span>
            <button class="close-btn" aria-label="Close" @click=${this.handleCloseClick}>
              <img src="/vanilla/ui/menu/x.svg" alt="" />
            </button>
          </div>
          <div class="hint">
            💡 You can also type the <strong>-name-</strong> instead of <strong>/name</strong> to use in chat
          </div>
          ${
            results.length > 0
              ? html`
                  <div class="grid scrollbar-thin">
                    ${results.map(
                      (emote, i) => html`
                        <div
                          class="item ${i === this.picker.selected ? 'selected' : ''}"
                          tabindex="0"
                          data-index=${i}
                          @click=${this.handleItemClick}
                          @keydown=${this.picker.handleItemKeyDown}
                          title="/${emote.name}"
                        >
                          <img src="/vanilla/ui/emotes/emote_${emote.name}.png" alt="${emote.name}" loading="lazy" />
                          <div class="label">/${emote.name}</div>
                        </div>
                      `,
                    )}
                  </div>
                `
              : html`<div class="no-results">No emotes matching "${this.picker.term}"</div>`
          }
        </div>
        <div class="search-box">
          <input
            type="text"
            placeholder="Search emotes..."
            .value=${this.picker.term}
            @input=${this.picker.handleInput}
            @keydown=${this.picker.handleSearchKeyDown}
          />
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'emotes-panel': EmotesPanel;
  }
}
